import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Stack, useTheme } from "@mui/material";
import { type FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import type { ClientPropertyTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { usePropertyValueMutation } from "web_myplann/Portfolio/Components/Redesign/Detail/Value/usePropertyValueMutation";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";
import { z } from "zod";

type Props = {
	open: boolean;
	onClose: () => void;
	propertyId: number;
	propertyType: ClientPropertyTypeEnum;
	valueLabel: string;
	value: number | null | undefined;
	currencySign: string;
};

type ValueFormModel = { value: number };

export const PropertyValueDrawer: FC<Props> = ({
	open,
	onClose,
	propertyId,
	propertyType,
	valueLabel,
	value,
	currencySign,
}) => {
	const { t } = useResource();
	const theme = useTheme();
	const { save, isPending } = usePropertyValueMutation(
		propertyId,
		propertyType,
	);

	const schema = z.object({
		value: z
			.number({
				required_error: t(Resources.Portfolio.AddShared.Validations.required),
				invalid_type_error: t(
					Resources.Portfolio.AddShared.Validations.required,
				),
			})
			.gt(0, t(Resources.PortfolioDetail.Property.ValueDrawer.positiveValue))
			.max(
				ValidationLimits.Amount.Max,
				t(Resources.Portfolio.AddShared.Validations.maxValue),
			),
	});

	const { control, handleSubmit, reset } = useForm<ValueFormModel>({
		resolver: zodResolver(schema),
		defaultValues: { value: value ?? undefined } as ValueFormModel,
	});

	useEffect(() => {
		if (open) {
			reset({ value: value ?? undefined } as ValueFormModel);
		}
	}, [open, value, reset]);

	const onSubmit = handleSubmit((formValues) => {
		save(formValues.value, () => {
			toast.success(
				t(Resources.PortfolioDetail.Property.ValueDrawer.savedToast),
			);
			onClose();
		});
	});

	return (
		<MyPlannDrawer
			anchor="bottom"
			open={open}
			onClose={onClose}
			title={t(
				value === null || value === undefined
					? Resources.PortfolioDetail.Property.ValueDrawer.fillTitle
					: Resources.PortfolioDetail.Property.ValueDrawer.updateTitle,
			)}
		>
			<Stack component="form" onSubmit={onSubmit} pb={theme.spacingTokens.lg}>
				<FlatRowFormInput
					control={control}
					name="value"
					type="number"
					label={valueLabel}
					placeholder={t(
						Resources.PortfolioDetail.Property.ValueDrawer.placeholder,
					)}
					suffix={currencySign}
					isLast
				/>
				<Stack px={theme.spacingTokens.lg} pt={theme.spacingTokens.lg}>
					<Button type="submit" variant="primary" loading={isPending}>
						{t(Resources.PortfolioDetail.Property.ValueDrawer.confirm)}
					</Button>
				</Stack>
			</Stack>
		</MyPlannDrawer>
	);
};
